import React, { useState } from 'react';
import { Info, ChevronDown, ChevronUp } from 'lucide-react';
import { XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Bar, ComposedChart, Line, Legend, BarChart } from 'recharts';
import { monthlyFinancialData2025, companyInfo, returnTypeColors } from '../../data/financialData';
import { colors, investmentData, calculateAggregatedTotals, curqlFunds, calculateCurqlTotals } from '../../utils/constants';

const Financial = () => {
  const [expandedRows, setExpandedRows] = useState({});
  const [chartView, setChartView] = useState('totals'); // 'totals', 'byCompany', 'byType'

  // Use centralized investment data
  const directInvestmentsSummary = calculateAggregatedTotals();
  const curqlTotals = calculateCurqlTotals();

  const formatCurrency = (value) => {
    if (value === 0) return '$0';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  const formatCurrencyK = (value) => {
    if (value === 0) return '$0';
    if (value >= 1000000) return `$${(value / 1000000).toFixed(1)}M`;
    return `$${Math.round(value / 1000)}K`;
  };

  const getStatusColor = (status) => {
    switch(status) {
      case 'Active': return 'bg-green-100 text-green-800';
      case 'Onboarding': return 'bg-yellow-100 text-yellow-800';
      case 'Hold': return 'bg-gray-100 text-gray-800';
      case 'Not Active': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getROIColor = (roi) => {
    if (roi > 1) return 'text-green-600 font-semibold';
    return 'text-gray-900';
  };

  const toggleRowExpanded = (id) => {
    setExpandedRows(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };

  // Prepare chart data based on view
  const getChartData = () => {
    if (chartView === 'totals') {
      return monthlyFinancialData2025.map(m => ({
        month: m.month,
        total: m.monthlyReturn,
        cumulative: m.cumulativeReturn
      }));
    } else if (chartView === 'byCompany') {
      return monthlyFinancialData2025.map(m => ({
        month: m.month,
        ...Object.keys(companyInfo).reduce((acc, key) => ({
          ...acc,
          [key]: m.byCompany[key]?.total || 0
        }), {})
      }));
    } else {
      return monthlyFinancialData2025.map(m => ({
        month: m.month,
        efficiency: m.efficiency,
        income: m.income,
        contract: m.contract
      }));
    }
  };

  // Calculate YTD totals
  const ytdTotals = monthlyFinancialData2025.reduce((acc, m) => ({
    total: acc.total + m.monthlyReturn,
    efficiency: acc.efficiency + m.efficiency,
    income: acc.income + m.income,
    contract: acc.contract + m.contract
  }), { total: 0, efficiency: 0, income: 0, contract: 0 });

  // Custom tooltip for the chart
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-4 rounded-lg shadow-lg border border-gray-200">
          <p className="font-semibold text-gray-900 mb-2">{label} 2025</p>
          {payload.map((entry, index) => (
            <p key={index} className="text-sm" style={{ color: entry.color }}>
              {entry.name}: {formatCurrency(entry.value)}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between">
        <h2 className="text-3xl font-bold text-gray-900">Financial Performance</h2>
        <div className="text-sm text-gray-500">Updated: November 2025</div>
      </div>

      {/* Large Chart Section */}
      <div className="bg-white p-6 rounded-xl border shadow-sm">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-xl font-semibold text-gray-900">2025 Monthly Returns</h3>
            <p className="text-sm text-gray-600">YTD Total: {formatCurrency(ytdTotals.total)}</p>
          </div>

          {/* Toggle Buttons */}
          <div className="flex bg-gray-100 rounded-lg p-1">
            <button
              onClick={() => setChartView('totals')}
              className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                chartView === 'totals'
                  ? 'bg-white shadow text-blue-700'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Totals
            </button>
            <button
              onClick={() => setChartView('byCompany')}
              className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                chartView === 'byCompany'
                  ? 'bg-white shadow text-blue-700'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              By Company
            </button>
            <button
              onClick={() => setChartView('byType')}
              className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                chartView === 'byType'
                  ? 'bg-white shadow text-blue-700'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              By Return Type
            </button>
          </div>
        </div>

        {/* Chart */}
        <div style={{ height: 450 }}>
          <ResponsiveContainer width="100%" height="100%">
            {chartView === 'totals' ? (
              <ComposedChart data={getChartData()}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                <XAxis dataKey="month" tick={{ fill: '#6B7280', fontSize: 12 }} />
                <YAxis
                  tickFormatter={formatCurrencyK}
                  tick={{ fill: '#6B7280', fontSize: 11 }}
                  yAxisId="left"
                />
                <YAxis
                  tickFormatter={formatCurrencyK}
                  tick={{ fill: '#6B7280', fontSize: 11 }}
                  yAxisId="right"
                  orientation="right"
                />
                <Tooltip content={<CustomTooltip />} />
                <Legend />
                <Bar
                  yAxisId="left"
                  dataKey="total"
                  name="Monthly Return"
                  fill={colors.lightBlue}
                  radius={[4, 4, 0, 0]}
                />
                <Line
                  yAxisId="right"
                  type="monotone"
                  dataKey="cumulative"
                  name="Cumulative"
                  stroke={colors.blue}
                  strokeWidth={3}
                  dot={{ fill: colors.blue, strokeWidth: 2 }}
                />
              </ComposedChart>
            ) : chartView === 'byCompany' ? (
              <BarChart data={getChartData()}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                <XAxis dataKey="month" tick={{ fill: '#6B7280', fontSize: 12 }} />
                <YAxis tickFormatter={formatCurrencyK} tick={{ fill: '#6B7280', fontSize: 11 }} />
                <Tooltip content={<CustomTooltip />} />
                <Legend />
                {Object.entries(companyInfo).map(([key, info]) => (
                  <Bar
                    key={key}
                    dataKey={key}
                    name={info.name}
                    stackId="stack"
                    fill={info.color}
                  />
                ))}
              </BarChart>
            ) : (
              <BarChart data={getChartData()}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                <XAxis dataKey="month" tick={{ fill: '#6B7280', fontSize: 12 }} />
                <YAxis tickFormatter={formatCurrencyK} tick={{ fill: '#6B7280', fontSize: 11 }} />
                <Tooltip content={<CustomTooltip />} />
                <Legend />
                <Bar dataKey="efficiency" name="Efficiency" stackId="stack" fill={returnTypeColors.efficiency} />
                <Bar dataKey="income" name="Income" stackId="stack" fill={returnTypeColors.income} />
                <Bar dataKey="contract" name="Contract" stackId="stack" fill={returnTypeColors.contract} />
              </BarChart>
            )}
          </ResponsiveContainer>
        </div>
      </div>

      {/* Monthly Data Table - Updates based on chart toggle */}
      <div className="bg-white rounded-xl border shadow-sm overflow-hidden">
        <div className="p-6 border-b" style={{ backgroundColor: colors.blue }}>
          <h3 className="text-xl font-semibold text-white">
            2025 Monthly Returns Detail
            {chartView === 'byCompany' && ' - By Company'}
            {chartView === 'byType' && ' - By Return Type'}
          </h3>
        </div>

        <div>
          <table className="w-full text-sm">
            <thead className="bg-gray-50">
              <tr className="text-left font-semibold text-gray-900">
                <th className="px-4 py-3">Month</th>
                {chartView === 'totals' && (
                  <>
                    <th className="px-4 py-3 text-right">Monthly Return</th>
                    <th className="px-4 py-3 text-right">Cumulative</th>
                  </>
                )}
                {chartView === 'byCompany' && (
                  <>
                    <th className="px-4 py-3 text-right">Confer</th>
                    <th className="px-4 py-3 text-right">CUautoFi</th>
                    <th className="px-4 py-3 text-right">ModernFi</th>
                    <th className="px-4 py-3 text-right">Posh</th>
                    <th className="px-4 py-3 text-right">Saris AI</th>
                    <th className="px-4 py-3 text-right">ScribeUp</th>
                    <th className="px-4 py-3 text-right font-bold border-l">Total</th>
                  </>
                )}
                {chartView === 'byType' && (
                  <>
                    <th className="px-4 py-3 text-right">Efficiency</th>
                    <th className="px-4 py-3 text-right">Income</th>
                    <th className="px-4 py-3 text-right">Contract</th>
                    <th className="px-4 py-3 text-right font-bold border-l">Total</th>
                  </>
                )}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {monthlyFinancialData2025.map((month, idx) => (
                <tr key={month.month} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                  <td className="px-4 py-3 font-medium text-gray-900">
                    {month.month}
                  </td>
                  {chartView === 'totals' && (
                    <>
                      <td className="px-4 py-3 text-right font-semibold text-gray-900">
                        {formatCurrency(month.monthlyReturn)}
                      </td>
                      <td className="px-4 py-3 text-right text-gray-900">
                        {formatCurrency(month.cumulativeReturn)}
                      </td>
                    </>
                  )}
                  {chartView === 'byCompany' && (
                    <>
                      <td className="px-4 py-3 text-right text-gray-900">
                        {formatCurrency(month.byCompany.confer.total)}
                      </td>
                      <td className="px-4 py-3 text-right text-gray-900">
                        {formatCurrency(month.byCompany.cuautofi.total)}
                      </td>
                      <td className="px-4 py-3 text-right text-gray-900">
                        {formatCurrency(month.byCompany.modernfi.total)}
                      </td>
                      <td className="px-4 py-3 text-right text-gray-900">
                        {formatCurrency(month.byCompany.posh.total)}
                      </td>
                      <td className="px-4 py-3 text-right text-gray-900">
                        {formatCurrency(month.byCompany.sarisai.total)}
                      </td>
                      <td className="px-4 py-3 text-right text-gray-900">
                        {formatCurrency(month.byCompany.scribeup.total)}
                      </td>
                      <td className="px-4 py-3 text-right font-bold border-l text-gray-900">
                        {formatCurrency(month.monthlyReturn)}
                      </td>
                    </>
                  )}
                  {chartView === 'byType' && (
                    <>
                      <td className="px-4 py-3 text-right text-gray-900">
                        {formatCurrency(month.efficiency)}
                      </td>
                      <td className="px-4 py-3 text-right text-gray-900">
                        {formatCurrency(month.income)}
                      </td>
                      <td className="px-4 py-3 text-right text-gray-900">
                        {formatCurrency(month.contract)}
                      </td>
                      <td className="px-4 py-3 text-right font-bold border-l text-gray-900">
                        {formatCurrency(month.monthlyReturn)}
                      </td>
                    </>
                  )}
                </tr>
              ))}

              {/* YTD Totals Row */}
              <tr className="bg-blue-50 font-bold border-t-2 border-gray-300">
                <td className="px-4 py-3 text-gray-900">YTD Total</td>
                {chartView === 'totals' && (
                  <>
                    <td className="px-4 py-3 text-right text-gray-900">
                      {formatCurrency(ytdTotals.total)}
                    </td>
                    <td className="px-4 py-3 text-right text-gray-900">
                      {formatCurrency(ytdTotals.total)}
                    </td>
                  </>
                )}
                {chartView === 'byCompany' && (
                  <>
                    <td className="px-4 py-3 text-right text-gray-900">
                      {formatCurrency(monthlyFinancialData2025.reduce((sum, m) => sum + m.byCompany.confer.total, 0))}
                    </td>
                    <td className="px-4 py-3 text-right text-gray-900">
                      {formatCurrency(monthlyFinancialData2025.reduce((sum, m) => sum + m.byCompany.cuautofi.total, 0))}
                    </td>
                    <td className="px-4 py-3 text-right text-gray-900">
                      {formatCurrency(monthlyFinancialData2025.reduce((sum, m) => sum + m.byCompany.modernfi.total, 0))}
                    </td>
                    <td className="px-4 py-3 text-right text-gray-900">
                      {formatCurrency(monthlyFinancialData2025.reduce((sum, m) => sum + m.byCompany.posh.total, 0))}
                    </td>
                    <td className="px-4 py-3 text-right text-gray-900">
                      {formatCurrency(monthlyFinancialData2025.reduce((sum, m) => sum + m.byCompany.sarisai.total, 0))}
                    </td>
                    <td className="px-4 py-3 text-right text-gray-900">
                      {formatCurrency(monthlyFinancialData2025.reduce((sum, m) => sum + m.byCompany.scribeup.total, 0))}
                    </td>
                    <td className="px-4 py-3 text-right border-l text-gray-900">
                      {formatCurrency(ytdTotals.total)}
                    </td>
                  </>
                )}
                {chartView === 'byType' && (
                  <>
                    <td className="px-4 py-3 text-right text-gray-900">
                      {formatCurrency(ytdTotals.efficiency)}
                    </td>
                    <td className="px-4 py-3 text-right text-gray-900">
                      {formatCurrency(ytdTotals.income)}
                    </td>
                    <td className="px-4 py-3 text-right text-gray-900">
                      {formatCurrency(ytdTotals.contract)}
                    </td>
                    <td className="px-4 py-3 text-right border-l text-gray-900">
                      {formatCurrency(ytdTotals.total)}
                    </td>
                  </>
                )}
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* Investment Portfolio Table */}
      <div className="bg-white rounded-xl border shadow-sm overflow-hidden">
        <div className="p-6 border-b" style={{backgroundColor: colors.blue}}>
          <h3 className="text-xl font-semibold text-white">Investment Portfolio</h3>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr className="text-left text-sm font-semibold text-gray-900">
                <th className="px-6 py-4">Investment</th>
                <th className="px-4 py-4 text-right">Invested</th>
                <th className="px-4 py-4 text-right">Current Value</th>
                <th className="px-4 py-4 text-right">Efficiency</th>
                <th className="px-4 py-4 text-right">Income</th>
                <th className="px-4 py-4 text-right">Contract</th>
                <th className="px-4 py-4 text-right font-bold">YTD Returns</th>
                <th className="px-4 py-4 text-center">ROI</th>
                <th className="px-2 py-4"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {investmentData.map((investment) => (
                <React.Fragment key={investment.id}>
                  <tr
                    className="hover:bg-gray-50 transition-colors cursor-pointer"
                    onClick={() => toggleRowExpanded(investment.id)}
                  >
                    <td className="px-6 py-4">
                      <div className="flex items-center space-x-3">
                        <div>
                          <div className="font-medium text-gray-900">{investment.name}</div>
                          <div className="text-xs text-gray-500">{investment.category}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-4 text-right font-medium text-gray-900">
                      {formatCurrency(investment.invested)}
                    </td>
                    <td className="px-4 py-4 text-right text-gray-900">
                      {formatCurrency(investment.currentValue)}
                    </td>
                    <td className="px-4 py-4 text-right text-gray-900">
                      {formatCurrency(investment.returns.efficiency)}
                    </td>
                    <td className="px-4 py-4 text-right text-gray-900">
                      {formatCurrency(investment.returns.income)}
                    </td>
                    <td className="px-4 py-4 text-right text-gray-900">
                      {formatCurrency(investment.returns.contract)}
                    </td>
                    <td className="px-4 py-4 text-right font-bold text-gray-900">
                      {formatCurrency(investment.returns.total)}
                    </td>
                    <td className={`px-4 py-4 text-center font-semibold ${getROIColor(investment.internalROI)}`}>
                      {investment.internalROI.toFixed(2)}x
                    </td>
                    <td className="px-2 py-4">
                      {expandedRows[investment.id] ?
                        <ChevronUp className="w-4 h-4 text-gray-400" /> :
                        <ChevronDown className="w-4 h-4 text-gray-400" />
                      }
                    </td>
                  </tr>
                  {expandedRows[investment.id] && (
                    <tr className="bg-gray-50">
                      <td colSpan="10" className="px-6 py-4">
                        <div className="flex items-start space-x-2">
                          <Info className="w-4 h-4 text-gray-400 mt-0.5" />
                          <div>
                            <p className="text-sm text-gray-600">{investment.description}</p>
                            <div className="mt-2 flex items-center space-x-6 text-xs text-gray-500">
                              <span>2025 Asset Value: {formatCurrency(investment.assetValue2025)}</span>
                              <span>•</span>
                              <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(investment.status)}`}>
                                {investment.status}
                              </span>
                            </div>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}

              {/* Summary Row */}
              <tr className="bg-blue-50 font-bold">
                <td className="px-6 py-4 text-gray-900">Direct Investments</td>
                <td className="px-4 py-4 text-right text-gray-900">
                  {formatCurrency(directInvestmentsSummary.invested)}
                </td>
                <td className="px-4 py-4 text-right text-gray-900">
                  {formatCurrency(directInvestmentsSummary.currentValue)}
                </td>
                <td className="px-4 py-4 text-right text-gray-900">
                  {formatCurrency(directInvestmentsSummary.efficiency)}
                </td>
                <td className="px-4 py-4 text-right text-gray-900">
                  {formatCurrency(directInvestmentsSummary.income)}
                </td>
                <td className="px-4 py-4 text-right text-gray-900">
                  {formatCurrency(directInvestmentsSummary.contract)}
                </td>
                <td className="px-4 py-4 text-right text-gray-900">
                  {formatCurrency(directInvestmentsSummary.total)}
                </td>
                <td className={`px-4 py-4 text-center ${getROIColor(directInvestmentsSummary.total / directInvestmentsSummary.invested)}`}>
                  {(directInvestmentsSummary.total / directInvestmentsSummary.invested).toFixed(2)}x
                </td>
                <td></td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* CURQL Funds Section */}
      <div className="bg-white rounded-xl border shadow-sm overflow-hidden">
        <div style={{backgroundColor: colors.blue}} className="p-6 border-b">
          <h3 className="text-xl font-semibold text-white">CURQL Fund Investments</h3>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-white">
              <tr className="text-left text-sm font-semibold text-gray-900">
                <th className="px-6 py-4">Fund Name</th>
                <th className="px-4 py-4 text-right">Commitment</th>
                <th className="px-4 py-4 text-right">Invested</th>
                <th className="px-4 py-4 text-right">2025 Asset Value</th>
                <th className="px-4 py-4 text-right">Performance</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {curqlFunds.map((fund, index) => {
                const performance = ((fund.currentValue - fund.invested) / fund.invested * 100).toFixed(1);
                return (
                  <tr key={index} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4 font-medium text-gray-900">{fund.name}</td>
                    <td className="px-4 py-4 text-right text-gray-900">
                      {formatCurrency(fund.commitment)}
                    </td>
                    <td className="px-4 py-4 text-right font-medium text-gray-900">
                      {formatCurrency(fund.invested)}
                    </td>
                    <td className="px-4 py-4 text-right font-semibold text-gray-900">
                      {formatCurrency(fund.currentValue)}
                    </td>
                    <td className="px-4 py-4 text-right font-semibold text-gray-900">
                      {performance > 0 ? '+' : ''}{performance}%
                    </td>
                  </tr>
                );
              })}

              {/* CURQL Totals Row */}
              <tr className="bg-blue-50 font-bold border-t-2 border-gray-300">
                <td className="px-6 py-4 text-gray-900">CURQL Funds Total</td>
                <td className="px-4 py-4 text-right text-gray-900">
                  {formatCurrency(curqlTotals.commitment)}
                </td>
                <td className="px-4 py-4 text-right text-gray-900">
                  {formatCurrency(curqlTotals.invested)}
                </td>
                <td className="px-4 py-4 text-right text-gray-900">
                  {formatCurrency(curqlTotals.currentValue)}
                </td>
                <td className="px-4 py-4 text-right text-gray-900">
                  {curqlTotals.invested > 0 ?
                    `${curqlTotals.currentValue > curqlTotals.invested ? '+' : ''}${(((curqlTotals.currentValue - curqlTotals.invested) / curqlTotals.invested) * 100).toFixed(1)}%` :
                    '0%'
                  }
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default Financial;